/**
 * Benefit-led copy for the generic plan-limit modal / screen (no feature-specific variant).
 * Phase C: lead with what the feature does and what Lite includes, then which plans unlock more.
 * Internal plan keys stay 'Free' | 'Starter' | 'Growth' | 'Pro' — display names come from planDisplay.
 * Do not put "upgrade" wording in GrowthEngineHumanDemoRow / GROWTH_ENGINE_HUMAN_PATH.
 */
import { normalizePlanKey, planDisplayName } from 'src/constants/planDisplay'

/**
 * @param {string|null|undefined} unlockPlan - Plan that unlocks the feature (e.g. 'Starter').
 *   Free / empty means "no specific plan" (a usage limit was hit).
 */
export function getDefaultPlanLimitCopy(unlockPlan) {
  const hasUnlockPlan =
    Boolean(unlockPlan) && normalizePlanKey(unlockPlan) !== 'Free'

  if (hasUnlockPlan) {
    const planName = planDisplayName(unlockPlan)
    return {
      title: `Unlock this with ${planName}`,
      text: `${planName} and above add this to everything ${planDisplayName(
        'Free',
      )} already gives you. Compare plans to see what else comes with it, or book a demo and we'll show you what fits your venue.`,
    }
  }

  return {
    title: "You've used this month's allowance",
    text: `${planDisplayName(
      'Free',
    )} covers everyday ordering. Starter, Growth, and Pro raise your allowances so invoices, recipes, and orders keep moving. Compare plans to see which fits, or book a demo.`,
  }
}

export const DEFAULT_PLAN_LIMIT_BENEFITS = Object.freeze([
  {
    title: 'Keep working without waiting',
    text: 'Higher monthly allowances for invoices, recipes, purchase orders, and more.',
  },
  {
    title: 'Operational tools',
    text: 'Gross Profit entry, menu costing, stock take, and goods receipting at scale.',
  },
  {
    title: 'Accounting sync',
    text: 'Connect Xero on Growth or Pro and send reconciled bills automatically.',
  },
  {
    title: 'Team controls',
    text: 'Custom roles, unlimited restricted suppliers, and approval workflows on paid plans.',
  },
])

export const DEFAULT_PLAN_LIMIT_NOTE = `Everyday ordering stays on ${planDisplayName(
  'Free',
)}: unlimited suppliers and past orders. Paid plans add allowances and tools on top. Questions? Email info@ordersmadesimple.com or book a demo.`
